import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import { Copy, Check, ChevronsLeftRight, Download } from 'lucide-react';
import Prism from 'prismjs';

// Import theme and required languages
import 'prismjs/themes/prism.css'; // Base theme, will be overridden by styled-components
import 'prismjs/components/prism-javascript';
import 'prismjs/components/prism-jsx';
import 'prismjs/components/prism-typescript';
import 'prismjs/components/prism-tsx';
import 'prismjs/components/prism-css';
import 'prismjs/components/prism-python';
import 'prismjs/components/prism-java';
import 'prismjs/components/prism-c';
import 'prismjs/components/prism-cpp';
import 'prismjs/components/prism-json';
import 'prismjs/components/prism-markup';
import 'prismjs/components/prism-sql';
import 'prismjs/components/prism-bash';
import 'prismjs/components/prism-yaml';
import 'prismjs/components/prism-markdown';

// Monospace fonts defined
const codeFontFamily = "'SF Mono', 'Menlo', 'Monaco', 'Consolas', 'Liberation Mono', 'Courier New', monospace";

// VS Code colors for light and dark mode - defined once to avoid recreation
const vsCodeColors = {
  light: {
    background: '#FFFFFF',
    headerBackground: '#F3F3F3',
    border: '#E0E0E0',
    text: '#000000',
    buttonBg: '#E2E2E2',
    buttonHover: '#D2D2D2',
    copySuccess: 'rgba(40, 167, 69, 0.15)',
    copySuccessHover: 'rgba(40, 167, 69, 0.2)',
    scrollbarTrack: 'rgba(0, 0, 0, 0.05)',
    scrollbarThumb: 'rgba(0, 0, 0, 0.1)',
    scrollbarThumbHover: 'rgba(0, 0, 0, 0.15)',
    expandButtonBg: '#F3F3F3',
    expandButtonHover: '#E5E5E5',
    lineNumbersBg: '#F8F8F8',
    lineNumbers: '#858585',
    lineNumbersHighlight: '#333333',
    syntax: {
      comment: '#008000',        // Green
      string: '#A31515',         // Red
      keyword: '#0000FF',        // Blue
      function: '#795E26',       // Brown
      number: '#098658',         // Dark green
      operator: '#000000',       // Black
      className: '#267F99',      // Teal
      variable: '#001080',       // Dark blue
      property: '#001080',       // Dark blue
      punctuation: '#000000',    // Black
      parameter: '#001080',      // Dark blue
      tag: '#800000',            // Dark brown
      attrName: '#FF0000',       // Red
      attrValue: '#0000FF',      // Blue
      regex: '#811F3F',          // Burgundy
      boolean: '#0000FF',        // Blue
    }
  },
  dark: {
    background: '#1E1E1E',
    headerBackground: '#252526',
    border: '#3E3E42',
    text: '#D4D4D4',
    buttonBg: '#3D3D3D',
    buttonHover: '#4B4B4B',
    copySuccess: 'rgba(40, 167, 69, 0.2)',
    copySuccessHover: 'rgba(40, 167, 69, 0.25)',
    scrollbarTrack: 'rgba(255, 255, 255, 0.04)',
    scrollbarThumb: 'rgba(255, 255, 255, 0.14)',
    scrollbarThumbHover: 'rgba(255, 255, 255, 0.2)',
    expandButtonBg: '#252526',
    expandButtonHover: '#2D2D2D',
    lineNumbersBg: '#1E1E1E',
    lineNumbers: '#6D6D6D',
    lineNumbersHighlight: '#CCCCCC',
    syntax: {
      comment: '#6A9955',        // Green
      string: '#CE9178',         // Orange
      keyword: '#569CD6',        // Blue
      function: '#DCDCAA',       // Yellow
      number: '#B5CEA8',         // Light green
      operator: '#D4D4D4',       // Light gray
      className: '#4EC9B0',      // Teal
      variable: '#9CDCFE',       // Light blue
      property: '#9CDCFE',       // Light blue
      punctuation: '#D4D4D4',    // Light gray
      parameter: '#9CDCFE',      // Light blue
      tag: '#569CD6',            // Blue
      attrName: '#9CDCFE',       // Light blue
      attrValue: '#CE9178',      // Orange
      regex: '#D16969',          // Red
      boolean: '#569CD6',        // Blue
    }
  }
};

// Styled components - defined outside the component to avoid recreation
const CodeLine = styled('span')(({ theme }) => {
  const isDarkMode = theme.palette.mode === 'dark';
  const colors = isDarkMode ? vsCodeColors.dark : vsCodeColors.light;
  
  return {
    display: 'block',
    width: '100%',
    maxWidth: '100%',
    opacity: 1, // Always visible
    color: colors.text,
    fontFamily: codeFontFamily,
    fontSize: '14px',
    lineHeight: '1.5',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
    overflowWrap: 'break-word',
  };
});

const CodeContainer = styled('div')(({ theme }) => {
  const isDarkMode = theme.palette.mode === 'dark';
  const colors = isDarkMode ? vsCodeColors.dark : vsCodeColors.light;
  
  return {
    position: 'relative',
    width: '100%',
    maxWidth: '100%',
    margin: '16px 0',
    borderRadius: '10px', // Más redondeado al estilo Big Sur
    overflow: 'hidden',
    backgroundColor: colors.background,
    border: `1px solid ${colors.border}`,
    fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    boxSizing: 'border-box',
    opacity: 1,
    overflowX: 'auto',
    // Sombra más suave al estilo macOS
    boxShadow: isDarkMode 
      ? '0 2px 12px rgba(0, 0, 0, 0.2)' 
      : '0 2px 12px rgba(0, 0, 0, 0.06)',
    transition: 'box-shadow 0.3s ease, transform 0.2s ease',
    '&:hover': {
      boxShadow: isDarkMode 
        ? '0 4px 20px rgba(0, 0, 0, 0.25)' 
        : '0 4px 20px rgba(0, 0, 0, 0.1)',
      transform: 'translateY(-1px)',
    },
  };
});

const HeaderContainer = styled('div')(({ theme }) => {
  const isDarkMode = theme.palette.mode === 'dark';
  const colors = isDarkMode ? vsCodeColors.dark : vsCodeColors.light;
  
  return {
    position: 'sticky',
    top: 0,
    zIndex: 10,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 12px',
    backgroundColor: isDarkMode ? 'rgba(50, 50, 50, 0.8)' : 'rgba(240, 240, 240, 0.8)',
    backdropFilter: 'blur(10px)',
    borderBottom: `1px solid ${colors.border}`,
    fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    boxSizing: 'border-box',
    width: '100%',
    // Sombra sutil para el header
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
  };
});

const HeaderButtons = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
});

const LanguageInfo = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  overflow: 'hidden',
});

const LanguageIcon = styled('div')(({ theme }) => {
  const isDarkMode = theme.palette.mode === 'dark';
  const colors = isDarkMode ? vsCodeColors.dark : vsCodeColors.light;
  
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: colors.text,
    opacity: 0.6,
  };
});

const LanguageLabel = styled('span')(({ theme }) => {
  const isDarkMode = theme.palette.mode === 'dark';
  const colors = isDarkMode ? vsCodeColors.dark : vsCodeColors.light;
  
  return {
    color: colors.text,
    fontSize: '14px',
    fontWeight: 500,
    fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    letterSpacing: '-0.01em',
  };
});

const ActionButton = styled('button')(({ theme, isCopied, isVSCode }) => {
  const isDarkMode = theme.palette.mode === 'dark';
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  const colors = isDarkMode ? vsCodeColors.dark : vsCodeColors.light;
  const wallpaperColor = '#2C2C2E';

  return {
    padding: '6px',
    borderRadius: '6px',
    border: isWallpaperMode && isVSCode ? '1px solid rgba(255, 255, 255, 0.2)' : 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
    backgroundColor: isCopied
      ? colors.copySuccess
      : isVSCode
        ? isWallpaperMode ? wallpaperColor : (isDarkMode ? '#0078D4' : '#0066B8')
        : isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
    backdropFilter: isWallpaperMode && isVSCode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode && isVSCode ? 'blur(10px)' : 'none',
    flexShrink: 0,
    '&:hover': {
      backgroundColor: isCopied
        ? colors.copySuccessHover
        : isVSCode
          ? isWallpaperMode ? '#3A3A3C' : (isDarkMode ? '#0085EA' : '#005BA4')
          : isDarkMode ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.1)',
      transform: 'translateY(-1px)',
    },
    '&:active': {
      transform: 'translateY(0px)',
      backgroundColor: isVSCode && isWallpaperMode ? '#48484A' : undefined,
    },
    fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    boxShadow: isDarkMode || (isWallpaperMode && isVSCode) ? 'none' : '0 1px 2px rgba(0, 0, 0, 0.05)',
  };
});

const CodeContentWrapper = styled('div')(({ isExpanded, codeLength, theme }) => {
  const isDarkMode = theme.palette.mode === 'dark';
  const colors = isDarkMode ? vsCodeColors.dark : vsCodeColors.light;
  
  return {
    display: 'flex',
    width: '100%',
    maxHeight: codeLength > 50 && !isExpanded ? '300px' : 'none',
    overflowY: codeLength > 50 && !isExpanded ? 'auto' : 'visible',
    position: 'relative', // Para el overlay
    '&::-webkit-scrollbar': {
      height: '8px',
      width: '8px',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: colors.scrollbarTrack,
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: colors.scrollbarThumb,
      borderRadius: '4px',
      '&:hover': {
        backgroundColor: colors.scrollbarThumbHover,
      },
    },
  };
});

const LineNumbers = styled('div')(({ theme }) => {
  const isDarkMode = theme.palette.mode === 'dark';
  const colors = isDarkMode ? vsCodeColors.dark : vsCodeColors.light;
  
  return {
    fontFamily: codeFontFamily,
    fontSize: '14px',
    lineHeight: '1.5',
    padding: '12px 8px 12px 12px',
    textAlign: 'right',
    backgroundColor: colors.lineNumbersBg,
    color: colors.lineNumbers,
    userSelect: 'none',
    minWidth: '40px',
    flexShrink: 0,
    position: 'sticky',
    left: 0,
    zIndex: 1,
  };
});

const CodeContent = styled('div')(({ theme }) => {
  const isDarkMode = theme.palette.mode === 'dark';
  const colors = isDarkMode ? vsCodeColors.dark : vsCodeColors.light;
  const syntax = colors.syntax;
  
  return {
    padding: '12px',
    textAlign: 'left',
    textShadow: 'none',
    boxSizing: 'border-box',
    width: '100%',
    maxWidth: '100%',
    position: 'relative',
    transformOrigin: 'top center',
    overflowX: 'visible',
    '& pre': {
      margin: 0,
      padding: 0,
      backgroundColor: 'transparent',
      fontFamily: codeFontFamily,
      fontSize: '14px',
      lineHeight: '1.5',
      letterSpacing: '0px',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      overflowWrap: 'break-word',
      maxWidth: '100%',
    },
    '& code': {
      display: 'block',
      fontFamily: codeFontFamily,
      fontSize: '14px',
      lineHeight: '1.5',
      letterSpacing: '0px',
      padding: 0,
      textAlign: 'left',
      backgroundColor: 'transparent',
      color: colors.text,
      fontSmoothing: 'antialiased',
      WebkitFontSmoothing: 'antialiased',
      MozOsxFontSmoothing: 'grayscale',
      tabSize: 4,
      textShadow: 'none',
      maxWidth: '100%',
      overflowWrap: 'break-word',
      wordBreak: 'break-all',
    },
    // VS Code-based syntax highlighting styles
    '& .token': {
      fontSize: '14px',
      lineHeight: '1.5',
      letterSpacing: '0px',
      fontFamily: codeFontFamily,
      textShadow: 'none',
      maxWidth: '100%',
      wordBreak: 'break-all',
      whiteSpace: 'pre-wrap',
    },
    '& .token.comment': { color: syntax.comment },
    '& .token.string': { color: syntax.string },
    '& .token.keyword': { color: syntax.keyword },
    '& .token.function': { color: syntax.function },
    '& .token.number': { color: syntax.number },
    '& .token.operator': { color: syntax.operator },
    '& .token.class-name': { color: syntax.className },
    '& .token.variable': { color: syntax.variable },
    '& .token.property': { color: syntax.property },
    '& .token.punctuation': { color: syntax.punctuation },
    '& .token.parameter': { color: syntax.parameter },
    '& .token.tag': { color: syntax.tag },
    '& .token.attr-name': { color: syntax.attrName },
    '& .token.attr-value': { color: syntax.attrValue },
    '& .token.regex': { color: syntax.regex },
    '& .token.boolean': { color: syntax.boolean },
  };
});

const ExpandButton = styled('button')(({ theme }) => {
  const isDarkMode = theme.palette.mode === 'dark';
  const colors = isDarkMode ? vsCodeColors.dark : vsCodeColors.light;
  
  return {
    width: '100%',
    padding: '6px',
    backgroundColor: colors.expandButtonBg,
    color: colors.text,
    border: 'none',
    borderTop: `1px solid ${colors.border}`,
    cursor: 'pointer',
    fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    fontSize: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      backgroundColor: colors.expandButtonHover,
    },
  };
});

// Nuevos componentes para las funcionalidades de desarrollador

// macOS Traffic Lights con funcionalidades para desarrolladores
const MacOSButtons = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  marginRight: '10px',
});

const TrafficLight = styled('button')(({ color, disabled, theme }) => ({
  width: '12px',
  height: '12px',
  borderRadius: '50%',
  backgroundColor: disabled ? `${color}80` : color, // Añadir transparencia cuando está deshabilitado
  border: 'none',
  boxShadow: 'inset 0 0 0 1px rgba(0, 0, 0, 0.1)',
  cursor: disabled ? 'not-allowed' : 'pointer',
  padding: 0,
  margin: 0,
  transition: 'all 0.2s ease',
  '&:hover': {
    filter: disabled ? 'none' : 'brightness(1.1)',
    transform: disabled ? 'none' : 'scale(1.08)',
  },
  '&:active': {
    filter: disabled ? 'none' : 'brightness(0.9)',
    transform: disabled ? 'none' : 'scale(0.95)',
  },
  position: 'relative',
  // Añadir tooltip para accesibilidad
  '&::after': {
    content: 'attr(data-tooltip)',
    position: 'absolute',
    top: '20px',
    left: '50%',
    transform: 'translateX(-50%)',
    padding: '4px 8px',
    borderRadius: '4px',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    color: 'white',
    fontSize: '11px',
    whiteSpace: 'nowrap',
    opacity: 0,
    visibility: 'hidden',
    transition: 'opacity 0.2s, visibility 0.2s',
    zIndex: 100,
    pointerEvents: 'none',
  },
  '&:hover::after': {
    opacity: 1,
    visibility: 'visible',
  },
}));

// Componente para explicaciones de código
const ExplanationsContainer = styled('div')(({ theme }) => {
  const isDarkMode = theme.palette.mode === 'dark';
  return {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '12px',
    backgroundColor: isDarkMode ? 'rgba(40,40,40,0.5)' : 'rgba(250,250,250,0.5)',
    borderRadius: '6px',
    margin: '6px 0',
    width: '100%',
  };
});

const ExplanationLine = styled('div')({
  display: 'flex',
  flexDirection: 'column',
});

const CodeLineText = styled('pre')(({ theme }) => {
  const isDarkMode = theme.palette.mode === 'dark';
  return {
    margin: 0,
    fontFamily: codeFontFamily,
    color: isDarkMode ? '#d4d4d4' : '#333',
    fontSize: '14px',
    lineHeight: '1.5',
  };
});

const ExplanationText = styled('div')(({ theme }) => {
  const isDarkMode = theme.palette.mode === 'dark';
  return {
    fontSize: '12px',
    color: isDarkMode ? '#9CDCFE' : '#0078D4',
    paddingLeft: '16px',
    borderLeft: `2px solid ${isDarkMode ? '#569CD6' : '#0078D4'}`,
    marginTop: '2px',
    marginLeft: '8px',
    lineHeight: '1.4',
  };
});

// Añadir este nuevo componente para el overlay de confirmación
const SuccessOverlay = styled('div')(({ theme, show }) => {
  const isDarkMode = theme.palette.mode === 'dark';
  return {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: show ? 'flex' : 'none',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: isDarkMode ? 'rgba(30, 30, 30, 0.7)' : 'rgba(255, 255, 255, 0.7)',
    backdropFilter: 'blur(3px)',
    zIndex: 50,
    borderRadius: '0 0 10px 10px', // Mantener las esquinas redondeadas
    animation: 'fadeInOut 1.5s forwards',
    // No incluir el header en el overlay
    marginTop: '0px', // Aproximadamente el alto del header
  };
});

const SuccessIcon = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '60px',
  height: '60px',
  borderRadius: '50%',
  backgroundColor: 'rgba(40, 167, 69, 0.2)',
  marginBottom: '12px',
});

const SuccessMessage = styled('div')(({ theme }) => {
  const isDarkMode = theme.palette.mode === 'dark';
  return {
    color: isDarkMode ? '#D4D4D4' : '#333333',
    fontSize: '16px',
    fontWeight: 500,
    fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  };
});

// Componente para el análisis de código
const CodeAnalysisResultContainer = styled('div')(({ theme }) => {
  const isDarkMode = theme.palette.mode === 'dark';
  return {
    padding: '12px',
    borderTop: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
    backgroundColor: isDarkMode ? 'rgba(30,30,30,0.8)' : 'rgba(245,245,245,0.8)',
    fontFamily: codeFontFamily,
    fontSize: '14px',
    lineHeight: '1.5',
    whiteSpace: 'pre-wrap',
    maxHeight: '200px',
    overflowY: 'auto',
    color: isDarkMode ? '#D4D4D4' : '#333333',
  };
});

// Notificación para formateo de código
const showNotification = (message, isDarkMode) => {
  const notification = document.createElement('div');
  notification.textContent = message;
  notification.style.position = 'fixed';
  notification.style.bottom = '20px';
  notification.style.right = '20px';
  notification.style.padding = '10px 15px';
  notification.style.backgroundColor = isDarkMode ? '#333' : '#f0f0f0';
  notification.style.color = isDarkMode ? '#fff' : '#000';
  notification.style.borderRadius = '5px';
  notification.style.boxShadow = '0 2px 10px rgba(0,0,0,0.2)';
  notification.style.zIndex = '9999';
  notification.style.animation = 'fadeInOut 3s forwards';
  
  const style = document.createElement('style');
  style.textContent = `
    @keyframes fadeInOut {
      0% { opacity: 0; transform: translateY(20px); }
      10% { opacity: 1; transform: translateY(0); }
      90% { opacity: 1; transform: translateY(0); }
      100% { opacity: 0; transform: translateY(-20px); }
    }
  `;
  
  document.head.appendChild(style);
  document.body.appendChild(notification);
  
  setTimeout(() => {
    document.body.removeChild(notification);
    document.head.removeChild(style);
  }, 3000);
};

// Fixed set of language mappings
const LANGUAGE_DISPLAY_MAP = {
  js: 'JavaScript',
  javascript: 'JavaScript',
  jsx: 'JavaScript JSX',
  ts: 'TypeScript',
  typescript: 'TypeScript',
  tsx: 'TypeScript JSX',
  py: 'Python',
  python: 'Python',
  java: 'Java',
  cpp: 'C++',
  'c++': 'C++',
  c: 'C',
  cs: 'C#',
  'c#': 'C#',
  csharp: 'C#',
  html: 'HTML',
  css: 'CSS',
  scss: 'SCSS',
  sass: 'Sass',
  less: 'Less',
  json: 'JSON',
  yaml: 'YAML',
  yml: 'YAML',
  sql: 'SQL',
  bash: 'Bash',
  sh: 'Shell',
  shell: 'Shell',
  powershell: 'PowerShell',
  ps: 'PowerShell',
  ps1: 'PowerShell',
  markdown: 'Markdown',
  md: 'Markdown',
  xml: 'XML',
  ruby: 'Ruby',
  rb: 'Ruby',
  php: 'PHP',
  go: 'Go',
  golang: 'Go',
  swift: 'Swift',
  kotlin: 'Kotlin',
  rust: 'Rust',
  rs: 'Rust',
  dart: 'Dart',
  plaintext: 'Plain Text',
  text: 'Plain Text',
  txt: 'Plain Text',
};

// File extension mapping for downloads
const FILE_EXTENSION_MAP = {
  js: 'js',
  javascript: 'js',
  jsx: 'jsx',
  ts: 'ts',
  typescript: 'ts',
  tsx: 'tsx',
  py: 'py',
  python: 'py',
  java: 'java',
  cpp: 'cpp',
  'c++': 'cpp',
  c: 'c',
  cs: 'cs',
  'c#': 'cs',
  csharp: 'cs',
  html: 'html',
  css: 'css',
  scss: 'scss',
  sass: 'sass',
  less: 'less',
  json: 'json',
  yaml: 'yml',
  yml: 'yml',
  sql: 'sql',
  bash: 'sh',
  sh: 'sh',
  shell: 'sh',
  powershell: 'ps1',
  ps: 'ps1',
  ps1: 'ps1',
  markdown: 'md',
  md: 'md',
  xml: 'xml',
  ruby: 'rb',
  rb: 'rb',
  php: 'php',
  go: 'go',
  golang: 'go',
  swift: 'swift',
  kotlin: 'kt',
  rust: 'rs',
  rs: 'rs',
  dart: 'dart',
  plaintext: 'txt',
  text: 'txt',
  txt: 'txt',
};

// Component to ensure global code styles
const GlobalCodeStyles = React.memo(() => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      .prism-code, .prism-code *, pre, pre *, code, code * {
        font-family: ${codeFontFamily} !important;
        max-width: 100% !important;
        overflow-wrap: break-word !important;
        word-break: break-all !important;
        text-shadow: none !important;
      }
      
      .prism-code a, code a {
        word-break: break-all !important;
      }
      
      .message-bubble {
        max-width: 100% !important;
        width: 100% !important;
        overflow-x: hidden !important;
      }
      
      .message-bubble .code-block-container {
        max-width: 100% !important;
        width: 100% !important;
      }
      
      .code-container {
        position: relative;
        overflow: hidden;
        width: 100% !important;
        max-width: 100% !important;
      }
      
      /* Ensure long lines wrap */
      .token {
        word-break: break-all !important;
        white-space: pre-wrap !important;
      }
      
      /* Keyframes para el overlay de éxito */
      @keyframes fadeInOut {
        0% { opacity: 0; }
        15% { opacity: 1; }
        85% { opacity: 1; }
        100% { opacity: 0; }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, [isDarkMode]);
  
  return null;
});

GlobalCodeStyles.displayName = 'GlobalCodeStyles';

// Optimized LineNumber component
const LineNumber = React.memo(({ index }) => (
  <div style={{ 
    fontFamily: codeFontFamily,
    fontSize: '14px',
    lineHeight: '1.5',
  }}>
    {index + 1}
  </div>
));

LineNumber.displayName = 'LineNumber';

// HighlightedLine component
const HighlightedLine = React.memo(({ line, grammar, lang, index, theme }) => {
  const highlightedHtml = useMemo(() => {
    try {
      return line.trim() === '' ? ' ' : Prism.highlight(line, grammar, lang);
    } catch (error) {
      console.error('Error highlighting line:', error);
      return line;
    }
  }, [line, grammar, lang]);

  return (
    <CodeLine 
      key={index} 
      theme={theme}
      dangerouslySetInnerHTML={{ __html: highlightedHtml }}
    />
  );
});

HighlightedLine.displayName = 'HighlightedLine';

// Loading placeholder for code lines
const LoadingPlaceholder = React.memo(({ isDarkMode, colors, dots }) => (
  <CodeContent>
    <pre>
      <div style={{ 
        color: isDarkMode ? colors.syntax.comment : colors.syntax.comment,
        fontFamily: codeFontFamily,
        marginBottom: '8px',
        fontSize: '14px',
        lineHeight: '1.5'
      }}>
        {`// Loading code${dots}`}
      </div>
      {Array.from({ length: 3 }).map((_, i) => (
        <div 
          key={i} 
          style={{
            height: '16px',
            width: `${Math.random() * 30 + 60}%`,
            backgroundColor: isDarkMode 
              ? 'rgba(255, 255, 255, 0.08)' 
              : 'rgba(0, 0, 0, 0.05)',
            marginBottom: '6px',
            borderRadius: '3px',
            animation: 'pulse 1.5s ease-in-out infinite',
            animationDelay: `${i * 0.1}s`,
          }}
        />
      ))}
      <style>{`
        @keyframes pulse {
          0% { opacity: 0.4; }
          50% { opacity: 0.7; }
          100% { opacity: 0.4; }
        }
      `}</style>
    </pre>
  </CodeContent>
));

LoadingPlaceholder.displayName = 'LoadingPlaceholder';

// Componente para las explicaciones de código
const CodeWithExplanations = React.memo(({ code, theme }) => {
  const lines = useMemo(() => code.split('\n'), [code]);
  
  // Función para generar explicaciones para líneas de código
  const getExplanationForLine = useCallback((line, index, totalLines) => {
    // Detectar patrones en el código
    const trimmedLine = line.trim();
    if (trimmedLine === '') return null;
    
    // Comentarios existentes
    const hasComment = trimmedLine.includes('//') || trimmedLine.includes('#');
    if ((hasComment && trimmedLine.startsWith('//')) || trimmedLine.startsWith('#')) {
      return null; // No explicar líneas que son solo comentarios
    }
    
    // Definición de función
    if (trimmedLine.includes('function ') || trimmedLine.match(/\b(def|fn|func)\b/) || 
        trimmedLine.match(/\b(class|interface|enum)\b/) || 
        trimmedLine.match(/^\s*[a-zA-Z0-9_$]+\s*\([^)]*\)\s*\{/) || 
        trimmedLine.match(/^\s*[a-zA-Z0-9_$]+\s*=\s*\([^)]*\)\s*=>/) ||
        trimmedLine.match(/^\s*[a-zA-Z0-9_$]+\s*=\s*function\s*\(/)) {
      return "This line defines a function or method that encapsulates a block of reusable code.";
    }
    
    // Bucles
    if (trimmedLine.match(/\b(for|while|foreach|do)\b/) && !trimmedLine.includes('//')) {
      return "This is a loop construct that repeats a block of code multiple times based on a condition.";
    }
    
    // Condicionales
    if ((trimmedLine.match(/\b(if|else|switch|case)\b/) || trimmedLine.includes('? :')) && !trimmedLine.includes('//')) {
      return "This conditional statement controls the flow of execution based on a condition.";
    }
    
    // Importaciones/Inclusiones
    if (trimmedLine.match(/\b(import|require|include|using|from)\b/) && !trimmedLine.includes('//')) {
      return "This line imports external code, libraries, or modules to use their functionality.";
    }
    
    // Retorno de valores
    if (trimmedLine.match(/\breturn\b/) && !trimmedLine.includes('//')) {
      return "This statement returns a value from a function and ends its execution.";
    }
    
    // Declaración de variables
    if (trimmedLine.match(/\b(var|let|const|int|float|double|string|boolean|char|bool)\b/) && !trimmedLine.includes('//')) {
      return "This line declares a variable that stores data for later use in the program.";
    }
    
    // Asignaciones
    if (trimmedLine.includes('=') && !trimmedLine.includes('==') && !trimmedLine.includes('===') && !trimmedLine.includes('<=') && !trimmedLine.includes('>=') && !trimmedLine.includes('!=')) {
      return "This assigns a value to a variable or property.";
    }
    
    // Llamadas a funciones
    if (trimmedLine.match(/[a-zA-Z0-9_$]+\s*\([^{]*\)/) && !trimmedLine.match(/\b(if|for|while|switch)\b/)) {
      return "This line calls a function to execute its code.";
    }
    
    // Líneas que cierran bloques
    if (trimmedLine === '}' || trimmedLine === 'end' || trimmedLine === 'fi' || trimmedLine === 'done') {
      return "This marks the end of a code block.";
    }
    
    // Operaciones matemáticas o lógicas complejas
    if ((trimmedLine.match(/[+\-*/%<>=&|^~]+/) && !trimmedLine.includes('//')) || 
        trimmedLine.match(/Math\.[a-zA-Z]+/)) {
      return "This line performs mathematical or logical operations.";
    }
    
    // Manejo de excepciones
    if (trimmedLine.match(/\b(try|catch|except|finally|throw|raise)\b/)) {
      return "This is part of exception handling logic to manage errors gracefully.";
    }
    
    // Operaciones con arrays/listas
    if (trimmedLine.match(/\[\s*\d+\s*\]/) || trimmedLine.includes('push(') || 
        trimmedLine.includes('pop(') || trimmedLine.includes('shift(') || 
        trimmedLine.includes('unshift(') || trimmedLine.includes('splice(') || 
        trimmedLine.includes('slice(') || trimmedLine.includes('append(') || 
        trimmedLine.includes('insert(')) {
      return "This line works with arrays or lists to store or manipulate collections of data.";
    }
    
    // Si es la última línea y no hemos identificado nada específico
    if (index === totalLines - 1) {
      return "This is the final line of the code snippet.";
    }
    
    // Para algunas líneas aleatorias sin explicación específica
    if (Math.random() > 0.7) {
      const genericExplanations = [
        "This line contributes to the program's functionality.",
        "This statement is part of the program's logic flow.",
        "This code handles data processing within the application.",
        "This instruction is executed as part of the program sequence."
      ];
      return genericExplanations[Math.floor(Math.random() * genericExplanations.length)];
    }
    
    // Para el resto de líneas, no dar explicación
    return null;
  }, []);
  
  return (
    <ExplanationsContainer>
      {lines.map((line, index) => {
        const explanation = getExplanationForLine(line, index, lines.length);
        
        return (
          <ExplanationLine key={index}>
            <CodeLineText theme={theme}>
              {line || ' '}
            </CodeLineText>
            {explanation && (
              <ExplanationText theme={theme}>
                {explanation}
              </ExplanationText>
            )}
          </ExplanationLine>
        );
      })}
    </ExplanationsContainer>
  );
});

CodeWithExplanations.displayName = 'CodeWithExplanations';

// Función para formatear código según el lenguaje
const formatCodeByLanguage = (code, language) => {
  // Esta es una implementación simplificada. En producción,
  // se utilizaría una biblioteca de formateo real como prettier, js-beautify, etc.
  
  // Función básica de formateo para demostración
  const indentCode = (code) => {
    const lines = code.split('\n');
    let indentLevel = 0;
    const indentSize = 2;
    const result = [];
    
    for (let line of lines) {
      const trimmedLine = line.trim();
      
      // Reducir indentación para líneas que cierran bloques
      if (trimmedLine.startsWith('}') || 
          trimmedLine === 'end' || 
          trimmedLine === 'fi' || 
          trimmedLine === 'done' ||
          trimmedLine.startsWith(')')) {
        indentLevel = Math.max(0, indentLevel - 1);
      }
      
      // Añadir la línea con la indentación correcta
      if (trimmedLine !== '') {
        result.push(' '.repeat(indentLevel * indentSize) + trimmedLine);
      } else {
        result.push('');
      }
      
      // Aumentar indentación después de abrir un bloque
      if (trimmedLine.endsWith('{') || 
          trimmedLine.endsWith(':') ||
          trimmedLine === 'begin' || 
          trimmedLine.startsWith('if ') ||
          trimmedLine.startsWith('for ') ||
          trimmedLine.startsWith('while ') ||
          trimmedLine.startsWith('function ') ||
          trimmedLine.startsWith('def ') ||
          trimmedLine.startsWith('class ')) {
        indentLevel++;
      }
    }
    
    return result.join('\n');
  };
  
  // Eliminar espacios en blanco múltiples
  let formatted = code.replace(/[ \t]+/g, ' ');
  
  // Asegurar que hay un espacio después de comas
  formatted = formatted.replace(/,([^\s])/g, ', $1');
  
  // Asegurar que hay un espacio alrededor de operadores
  formatted = formatted.replace(/([+\-*/%=<>!&|^])([\w\d(])/g, '$1 $2');
  formatted = formatted.replace(/([\w\d)])([+\-*/%=<>!&|^])/g, '$1 $2');
  
  // Aplicar indentación
  formatted = indentCode(formatted);
  
  return formatted;
};

// Función para analizar el código
const analyzeCode = (code, language) => {
  // Esta es una implementación simplificada. En un caso real,
  // utilizaríamos herramientas como ESLint, Pylint, etc.
  
  const results = [];
  const lines = code.split('\n');
  
  // Análisis básico según el lenguaje
  if (language === 'javascript' || language === 'js' || language === 'jsx') {
    // Verificar uso de var (mejor usar let/const)
    if (code.includes('var ')) {
      results.push('⚠️ Consider using let/const instead of var for better scoping');
    }
    
    // Verificar posibles errores comunes
    if (code.includes('==') && !code.includes('===')) {
      results.push('⚠️ Consider using === instead of == for strict equality comparison');
    }
    
    // Verificar console.log olvidados
    const consoleCount = (code.match(/console\.log/g) || []).length;
    if (consoleCount > 0) {
      results.push(`⚠️ Found ${consoleCount} console.log statement(s) that might be left in production code`);
    }
  } 
  else if (language === 'python' || language === 'py') {
    // Verificar imports no utilizados
    const importLines = lines.filter(line => line.trim().startsWith('import ') || line.trim().startsWith('from '));
    if (importLines.length > 5) {
      results.push('⚠️ Large number of imports detected. Consider organizing or removing unused ones');
    }
    
    // Verificar uso de print (mejor usar logging)
    const printCount = (code.match(/print\(/g) || []).length;
    if (printCount > 3) {
      results.push(`⚠️ Found ${printCount} print statements. Consider using a logging framework instead`);
    }
  }
  
  // Análisis genérico para cualquier lenguaje
  
  // Complejidad basada en la longitud
  if (lines.length > 100) {
    results.push('⚠️ Code is quite long (100+ lines). Consider breaking it into smaller functions or modules');
  }
  
  // Complejidad basada en líneas largas
  const longLines = lines.filter(line => line.length > 100);
  if (longLines.length > 0) {
    results.push(`⚠️ Found ${longLines.length} very long line(s). Consider breaking them for better readability`);
  }
  
  // Verificar comentarios
  const commentLines = lines.filter(line => 
    line.trim().startsWith('//') || 
    line.trim().startsWith('#') || 
    line.trim().startsWith('/*') ||
    line.trim().startsWith('*')
  );
  const commentRatio = commentLines.length / lines.length;
  
  if (commentRatio < 0.05 && lines.length > 30) {
    results.push('⚠️ Low comment density. Consider adding more comments to explain complex logic');
  }
  
  // Indentación inconsistente
  let hasInconsistentIndent = false;
  let prevIndent = -1;
  let indentType = null; // 'space' o 'tab'
  
  for (const line of lines) {
    if (line.trim() === '') continue;
    
    const indent = line.length - line.trimStart().length;
    if (indent > 0) {
      if (indentType === null) {
        indentType = line[0] === '\t' ? 'tab' : 'space';
      } else {
        const currentType = line[0] === '\t' ? 'tab' : 'space';
        if (currentType !== indentType) {
          hasInconsistentIndent = true;
          break;
        }
      }
      
      if (prevIndent >= 0) {
        const diff = Math.abs(indent - prevIndent);
        if (diff > 0 && diff !== 2 && diff !== 4 && diff !== 1) {
          hasInconsistentIndent = true;
          break;
        }
      }
      prevIndent = indent;
    }
  }
  
  if (hasInconsistentIndent) {
    results.push('⚠️ Inconsistent indentation detected. Consider using a code formatter');
  }
  
  // Evaluación final
  let qualityScore = 10; // Comenzamos con puntuación perfecta
  qualityScore -= results.length * 0.5; // Cada problema reduce la puntuación
  qualityScore = Math.max(1, Math.min(10, qualityScore)); // Limitar entre 1 y 10
  
  if (results.length === 0) {
    results.push('✅ No issues found. Code looks clean!');
  }
  
  return {
    score: qualityScore.toFixed(1),
    issues: results,
    summary: `Code Quality Score: ${qualityScore.toFixed(1)}/10`,
  };
};

// The main component
const AnimatedCodeBlock = ({ 
  code, 
  language, 
  shouldAnimate,
  onAnimationComplete,
  isLoading = false
}) => {
  // State hooks
  const [isCopied, setIsCopied] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [dots, setDots] = useState('');
  
  // Estados para las funcionalidades
  const [isFormatting, setIsFormatting] = useState(false);
  const [showExplanations, setShowExplanations] = useState(false);
  const [formattedCode, setFormattedCode] = useState(null);
  
  // Nuevos estados para las funcionalidades adicionales
  const [showFormatSuccess, setShowFormatSuccess] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  
  // Refs
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const minLoadingTimeoutRef = useRef(null);
  
  // Theme
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const colors = useMemo(() => isDarkMode ? vsCodeColors.dark : vsCodeColors.light, [isDarkMode]);
  
  // Memoized values
  const codeToDisplay = useMemo(() => formattedCode || code, [formattedCode, code]);
  const codeLines = useMemo(() => codeToDisplay.split('\n'), [codeToDisplay]);
  const lineCount = useMemo(() => codeLines.length, [codeLines]);
  const normalizedLanguage = useMemo(() => language?.toLowerCase() || 'plaintext', [language]);
  
  // Get language for Prism
  const prismLanguage = useMemo(() => {
    const lang = normalizedLanguage;
    return Prism.languages[lang] || Prism.languages.plaintext;
  }, [normalizedLanguage]);
  
  // Displayed language name
  const displayLanguage = useMemo(() => {
    return LANGUAGE_DISPLAY_MAP[normalizedLanguage] || 
           LANGUAGE_DISPLAY_MAP[normalizedLanguage.split('-')[0]] || 
           normalizedLanguage || 
           'Plain Text';
  }, [normalizedLanguage]);
  
  // Animation for loading dots
  useEffect(() => {
    if (isLoading) {
      const interval = setInterval(() => {
        setDots(prev => prev.length >= 3 ? '' : prev + '.');
      }, 300);
      return () => clearInterval(interval);
    }
  }, [isLoading]);
  
  // Minimum loading time
  useEffect(() => {
    if (isLoading) {
      minLoadingTimeoutRef.current = setTimeout(() => {
        // Just to ensure minimum loading time
      }, 800);
      
      return () => {
        if (minLoadingTimeoutRef.current) {
          clearTimeout(minLoadingTimeoutRef.current);
        }
      };
    }
  }, [isLoading]);
  
  // Call animation complete callback
  useEffect(() => {
    if (onAnimationComplete && !isLoading) {
      onAnimationComplete();
    }
  }, [onAnimationComplete, isLoading]);
  
  // Adjust size effect
  useEffect(() => {
    const adjustSize = () => {
      if (containerRef.current) {
        const parentElement = containerRef.current.parentElement;
        if (!parentElement) return;
        
        const parentWidth = parentElement.clientWidth;
        const computedStyle = window.getComputedStyle(parentElement);
        const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;
        const paddingRight = parseFloat(computedStyle.paddingRight) || 0;
        
        const maxAvailableWidth = parentWidth - paddingLeft - paddingRight;
        containerRef.current.style.maxWidth = `${maxAvailableWidth}px`;
        
        const codeElement = containerRef.current.querySelector('code');
        if (codeElement) {
          codeElement.style.maxWidth = `${maxAvailableWidth - 24}px`;
          codeElement.style.overflowWrap = 'break-word';
          codeElement.style.wordBreak = 'break-all';
        }
      }
    };
    
    // Run immediately
    adjustSize();
    
    // Add event listener for window resize
    window.addEventListener('resize', adjustSize);
    
    // Run again after a short delay to handle any dynamic changes
    const timeoutId = setTimeout(adjustSize, 100);
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', adjustSize);
      clearTimeout(timeoutId);
    };
  }, []);

  // Event handlers with useCallback
  const handleCopyClick = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(codeToDisplay);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      console.error('Error copying text:', error);
    }
  }, [codeToDisplay]);

  const handleDownloadCode = useCallback(() => {
    try {
      // Generate filename based on language
      const ext = FILE_EXTENSION_MAP[normalizedLanguage] || 'txt';
      const fileName = `code-snippet.${ext}`;
      
      // Create and download file
      const blob = new Blob([codeToDisplay], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Could not download the file. Code has been copied to clipboard instead.');
      // Copy as fallback
      navigator.clipboard.writeText(codeToDisplay).catch(e => console.error('Error copying:', e));
    }
  }, [codeToDisplay, normalizedLanguage]);

  const toggleExpand = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  // Modificar la función de formateo
  const handleFormatCode = useCallback(() => {
    if (isFormatting) return;
    
    setIsFormatting(true);
    
    // Simular un pequeño retardo para dar feedback visual
    setTimeout(() => {
      try {
        const formatted = formatCodeByLanguage(code, normalizedLanguage);
        setFormattedCode(formatted);
        
        // Mostrar el overlay de éxito en lugar de la notificación
        setShowFormatSuccess(true);
        setTimeout(() => setShowFormatSuccess(false), 1500);
      } catch (error) {
        console.error('Error formatting code:', error);
        // Para errores podemos mantener una notificación o implementar un overlay de error
        showNotification('Error formatting code', isDarkMode);
      } finally {
        setIsFormatting(false);
      }
    }, 800);
  }, [code, normalizedLanguage, isDarkMode, isFormatting]);
  
  const toggleExplanations = useCallback(() => {
    setShowExplanations(prev => !prev);
  }, []);

  // Nueva función para analizar el código
  const handleAnalyzeCode = useCallback(() => {
    if (isAnalyzing) return;
    
    setIsAnalyzing(true);
    setAnalysisResult(null);
    
    // Simular un pequeño retardo para el análisis
    setTimeout(() => {
      try {
        const result = analyzeCode(codeToDisplay, normalizedLanguage);
        setAnalysisResult(result);
      } catch (error) {
        setAnalysisResult({
          score: '0.0',
          issues: [`Error analyzing code: ${error.message}`],
          summary: 'Analysis failed'
        });
      } finally {
        setIsAnalyzing(false);
      }
    }, 1000);
  }, [codeToDisplay, normalizedLanguage, isAnalyzing]);

  // Memoized rendering components
  const renderLineNumbers = useMemo(() => (
    <LineNumbers>
      {codeLines.map((_, index) => (
        <LineNumber key={index} index={index} />
      ))}
    </LineNumbers>
  ), [codeLines]);

  const renderCodeContent = useMemo(() => (
    <CodeContent ref={contentRef}>
      <pre>
        {codeLines.map((line, index) => (
          <HighlightedLine 
            key={index}
            line={line}
            grammar={prismLanguage}
            lang={normalizedLanguage}
            index={index}
            theme={theme}
          />
        ))}
      </pre>
    </CodeContent>
  ), [codeLines, prismLanguage, normalizedLanguage, theme]);

  const renderLoadingState = useMemo(() => {
    // Line numbers placeholder
    const lineNumbersPlaceholder = (
      <LineNumbers>
        {Array.from({ length: 3 }).map((_, i) => (
          <LineNumber key={i} index={i} />
        ))}
      </LineNumbers>
    );
    
    return (
      <CodeContentWrapper isExpanded={true} codeLength={3} theme={theme}>
        {lineNumbersPlaceholder}
        <LoadingPlaceholder isDarkMode={isDarkMode} colors={colors} dots={dots} />
      </CodeContentWrapper>
    );
  }, [theme, isDarkMode, colors, dots]);

  // Memoized icon colors
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  const iconColor = useMemo(() =>
    isWallpaperMode ? '#FFFFFF' : (isDarkMode ? vsCodeColors.dark.text : vsCodeColors.light.text),
  [isDarkMode, isWallpaperMode]);

  const successColor = '#28A744';
  const vscodeIconColor = '#FFFFFF';

  // macOS traffic lights con funcionalidades para desarrolladores
  const renderMacOSButtons = useMemo(() => (
    <MacOSButtons>
      <TrafficLight 
        color="#FF5F56" 
        onClick={handleFormatCode} 
        disabled={isFormatting}
        data-tooltip={isFormatting ? "Formatting..." : "Format code"}
        aria-label={isFormatting ? "Formatting code" : "Format code"}
      />
      <TrafficLight 
        color="#FFBD2E" 
        onClick={toggleExplanations} 
        data-tooltip={showExplanations ? "Hide explanations" : "Show explanations"}
        aria-label={showExplanations ? "Hide explanations" : "Show explanations"}
      />
      <TrafficLight 
        color="#27C93F" 
        onClick={handleAnalyzeCode} 
        disabled={isAnalyzing}
        data-tooltip={isAnalyzing ? "Analyzing..." : "Analyze code quality"}
        aria-label={isAnalyzing ? "Analyzing code" : "Analyze code quality"}
      />
    </MacOSButtons>
  ), [
    handleFormatCode, 
    toggleExplanations, 
    handleAnalyzeCode, 
    isFormatting, 
    showExplanations, 
    isAnalyzing
  ]);

  return (
    <>
      <GlobalCodeStyles />
      <CodeContainer ref={containerRef} className="code-container">
        <HeaderContainer>
          {renderMacOSButtons}
          <LanguageInfo>
            <LanguageIcon>
              <ChevronsLeftRight size={14} />
            </LanguageIcon>
            <LanguageLabel>{displayLanguage}</LanguageLabel>
          </LanguageInfo>
          
          {!isLoading && (
            <HeaderButtons>
              <ActionButton 
                onClick={handleDownloadCode} 
                isVSCode={true}
                title="Download code file"
                aria-label="Download code file"
              >
                <Download size={14} style={{ color: vscodeIconColor }} />
              </ActionButton>
              <ActionButton 
                onClick={handleCopyClick} 
                isCopied={isCopied}
                title={isCopied ? "Copied" : "Copy code"}
                aria-label={isCopied ? "Copied" : "Copy code"}
              >
                {isCopied ? (
                  <Check size={14} style={{ color: successColor }} />
                ) : (
                  <Copy size={14} style={{ color: iconColor }} />
                )}
              </ActionButton>
            </HeaderButtons>
          )}
        </HeaderContainer>

        {isLoading ? renderLoadingState : (
          <>
            <CodeContentWrapper 
              isExpanded={isExpanded}
              codeLength={lineCount}
              theme={theme}
            >
              {renderLineNumbers}
              {showExplanations ? (
                <CodeWithExplanations code={codeToDisplay} theme={theme} />
              ) : (
                renderCodeContent
              )}
              
              {/* Overlay de éxito para formateo */}
              <SuccessOverlay theme={theme} show={showFormatSuccess}>
                <SuccessIcon>
                  <Check size={32} style={{ color: '#28A744' }} />
                </SuccessIcon>
                <SuccessMessage theme={theme}>
                  Code formatted successfully
                </SuccessMessage>
              </SuccessOverlay>
            </CodeContentWrapper>
            
            {/* Resultado de análisis */}
            {analysisResult && (
              <CodeAnalysisResultContainer theme={theme}>
                {isAnalyzing ? 'Analyzing code...' : (
                  <>
                    <strong>{analysisResult.summary}</strong>
                    <ul style={{ 
                      margin: '8px 0', 
                      paddingLeft: '20px', 
                      listStyleType: 'none' 
                    }}>
                      {analysisResult.issues.map((issue, index) => (
                        <li key={index} style={{ 
                          marginBottom: '4px',
                          display: 'flex',
                          alignItems: 'flex-start'
                        }}>
                          {issue}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </CodeAnalysisResultContainer>
            )}
          </>
        )}
        
        {!isLoading && lineCount > 50 && (
          <ExpandButton 
            onClick={toggleExpand}
            aria-label={isExpanded ? 'Show less code' : 'Show more code'}
            title={isExpanded ? 'Show less code' : 'Show more code'}
          >
            {isExpanded ? 'Show less' : 'Show more'}
          </ExpandButton>
        )}
      </CodeContainer>
    </>
  );
};

// Enhanced memoization
export default React.memo(AnimatedCodeBlock, (prevProps, nextProps) => {
  // Only re-render if these props change
  return (
    prevProps.code === nextProps.code &&
    prevProps.language === nextProps.language &&
    prevProps.isLoading === nextProps.isLoading
  );
});